import { useMemo } from "react";

export type TripMode = "planning" | "traveling" | "settlement";

interface UseTripModeOptions {
  startDate: string | null;
  endDate: string | null;
  /** Override automatic mode detection */
  manualMode?: TripMode | null;
}

interface UseTripModeResult {
  mode: TripMode;
  /** Days until trip starts (negative if already started) */
  daysUntilStart: number | null;
  /** Days until trip ends (negative if already ended) */
  daysUntilEnd: number | null;
  /** Whether the trip is happening today */
  isToday: boolean;
  /** Whether trip dates are set */
  hasDates: boolean;
}

/**
 * Calculate the number of days between two dates (ignoring time)
 * Positive means futureDate is in the future
 */
function daysBetween(fromDate: Date, toDate: Date): number {
  const from = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate());
  const to = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate());
  const diffTime = to.getTime() - from.getTime();
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

/**
 * Check if a date is today
 */
function isDateToday(date: Date): boolean {
  const today = new Date();
  return (
    date.getFullYear() === today.getFullYear() &&
    date.getMonth() === today.getMonth() &&
    date.getDate() === today.getDate()
  );
}

/**
 * Determines the current trip mode based on trip dates
 *
 * Modes:
 * - planning: Before trip starts (or no dates set)
 * - traveling: During the trip (between start and end dates, inclusive)
 * - settlement: After trip ends
 */
export function useTripMode({
  startDate,
  endDate,
  manualMode,
}: UseTripModeOptions): UseTripModeResult {
  return useMemo(() => {
    // If manual mode is set, use it
    if (manualMode) {
      return {
        mode: manualMode,
        daysUntilStart: startDate ? daysBetween(new Date(), new Date(startDate)) : null,
        daysUntilEnd: endDate ? daysBetween(new Date(), new Date(endDate)) : null,
        isToday: false,
        hasDates: !!(startDate && endDate),
      };
    }

    // If no dates, default to planning mode
    if (!startDate || !endDate) {
      return {
        mode: "planning",
        daysUntilStart: null,
        daysUntilEnd: null,
        isToday: false,
        hasDates: false,
      };
    }

    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);

    const daysUntilStart = daysBetween(today, start);
    const daysUntilEnd = daysBetween(today, end);

    // Check if today falls within the trip dates
    const isWithinTrip = daysUntilStart <= 0 && daysUntilEnd >= 0;
    const isTodayTripDay = isDateToday(start) || isDateToday(end);

    let mode: TripMode;
    if (daysUntilStart > 0) {
      // Trip hasn't started yet
      mode = "planning";
    } else if (isWithinTrip) {
      // Currently on the trip
      mode = "traveling";
    } else {
      // Trip has ended
      mode = "settlement";
    }

    return {
      mode,
      daysUntilStart,
      daysUntilEnd,
      isToday: isTodayTripDay || isWithinTrip,
      hasDates: true,
    };
  }, [startDate, endDate, manualMode]);
}
